import React, { useCallback, useState, memo } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  CustomIconButton,
  Tooltip,
  ErrorButtonContained,
  PrimaryButtonContained,
} from "../../../components";
import {
  Dialog,
  DialogContent,
  DialogActions,
  DialogContentText,
  DialogTitle,
} from "@material-ui/core";
import {
  Visibility,
  Edit,
  DeleteForever,
  FileCopy,
  FolderOpenOutlined,
} from "../../../icons";
import { useTranslation } from "react-i18next";
import useAuthorized, {
  TEMPLATE_ARCHIVE_PERMISSION,
  TEMPLATE_DELETE_PERMISSION,
  TEMPLATE_UPDATE_PERMISSION,
} from "../../../hooks/useAuthorized";

function MasterTemplatesActions({ masterTemplate, onDelete, onClone }) {
  const { _id, userId, status } = masterTemplate;
  const { t } = useTranslation();
  const [cloneDialog, setCloneDialog] = useState(false);
  const hasArchivePermission = useAuthorized(TEMPLATE_ARCHIVE_PERMISSION);
  const hasDeletePermission = useAuthorized(TEMPLATE_DELETE_PERMISSION);
  const hasEditPermission = useAuthorized(TEMPLATE_UPDATE_PERMISSION);

  const isOwner = useSelector((state) => state.user.id === userId);
  const canEdit = isOwner || hasEditPermission;
  const canDelete = isOwner || hasDeletePermission;
  const canArchive = isOwner || hasArchivePermission;

  const isTemplateLocked = status === "LOCKED";
  const EditIcon = canEdit && !isTemplateLocked ? Edit : Visibility;

  const _onDelete = useCallback(() => onDelete(masterTemplate), [
    onDelete,
    masterTemplate,
  ]);

  const _onClone = useCallback(async () => {
    await onClone(masterTemplate);
    setCloneDialog(false);
  }, [onClone, masterTemplate, setCloneDialog]);

  return (
    <>
      <Tooltip
        title={
          canEdit && !isTemplateLocked ? t("Edit template") : t("View template")
        }
      >
        <CustomIconButton
          data-test-id={`template-action-edit-${_id}`}
          component={Link}
          to={`/master-templates/${_id}`}
          size="small"
        >
          <EditIcon fontSize="small" />
        </CustomIconButton>
      </Tooltip>

      {(canArchive || canDelete) && (
        <Tooltip title={isTemplateLocked ? t("Archive") : t("Delete template")}>
          <CustomIconButton
            data-test-id={`template-action-delete-${_id}`}
            size="small"
            color="error"
            onClick={_onDelete}
          >
            {canArchive && isTemplateLocked && (
              <FolderOpenOutlined fontSize="small" />
            )}
            {canDelete && !isTemplateLocked && (
              <DeleteForever fontSize="small" />
            )}
          </CustomIconButton>
        </Tooltip>
      )}

      <Tooltip title={t("Clone template")}>
        <CustomIconButton
          data-test-id={`template-action-copy-${_id}`}
          size="small"
          onClick={() => setCloneDialog(true)}
        >
          <FileCopy fontSize="small" />
        </CustomIconButton>
      </Tooltip>

      <Dialog
        open={cloneDialog}
        onClose={() => setCloneDialog(false)}
        aria-labelledby="clone-dialog-title"
        aria-describedby="clone-dialog-description"
      >
        <DialogTitle id="clone-dialog-title">{t("Clone template")}</DialogTitle>
        <DialogContent>
          <DialogContentText id="clone-dialog-description">
            {t("Are you sure you want to clone this template?")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <ErrorButtonContained onClick={_onClone} size="large">
            {t("Yes")}
          </ErrorButtonContained>
          <PrimaryButtonContained
            onClick={() => setCloneDialog(false)}
            size="large"
          >
            {t("No")}
          </PrimaryButtonContained>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default memo(MasterTemplatesActions);
